import { Drawer } from '@material-ui/core';
import React, { JSX, useEffect, useState } from 'react';

import { IInventoryRule, InventoryRuleType } from '~/src/dash/apiV2/inventory-rules/inventoryRules';
import { CloseButton } from '~/src/dash/components/buttons/commonButtons/CloseButton';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import { Panel } from '~/src/dash/components/layout/Panel';
import { borderRadius, ButtonV2, Col, Cols, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { useBaseState } from '~/src/dash/views/base/contexts';
import { ContentDataPreview } from '~/src/dash/views/inventory-rules-v2/Rules/common/ContentDataPreview';
import { CustomDataPreview } from '~/src/dash/views/inventory-rules-v2/Rules/common/CustomDataPreview';
import { InventoryRuleOverview } from '~/src/dash/views/inventory-rules-v2/Rules/InventoryRulesList/InventoryRuleDrawer/InventoryRuleOverview';
import { isEmpty } from 'lodash-es';
import { publicaColors } from '~/src/dash/utils/colors';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import { LoadingState } from '~/src/dash/utils/loading-states';
import {
  formatAdBreaksDataIntoLVTRows,
  IInventoryRulesListConfig,
} from '~/src/dash/views/inventory-rules-v2/Rules/InventoryRulesList/config';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  currentIndex: number | null;
  tableData: IInventoryRule[];
  loadState: LoadingState;
  cfg: IInventoryRulesListConfig;
}

export const InventoryRuleDrawer = ({
  isOpen,
  onClose,
  currentIndex,
  tableData,
  loadState,
  cfg,
}: IProps): JSX.Element => {
  const { totalWidth } = useBaseState();
  const adBreaksCfg = cfg.drawer.adBreaks;

  const [currentIdx, setCurrentIdx] = useState(currentIndex);
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);

  // Sync local state with prop when it changes
  useEffect(() => {
    setCurrentIdx(currentIndex);
  }, [currentIndex]);

  const rule = currentIdx !== null ? tableData[currentIdx] : null;

  const canNavigatePrev = currentIdx !== null && currentIdx > 0;
  const canNavigateNext = currentIdx !== null && currentIdx < tableData.length - 1;

  const rows = formatAdBreaksDataIntoLVTRows(rule?.AdBreaks || [], adBreaksCfg.columns);

  return (
    <Drawer open={isOpen} onClose={onClose} anchor='right'>
      <Panel basePadding style={{ width: `${(totalWidth || 800) * 0.6}px`, minWidth: '600px' }}>
        <Rows gap={spacing.base}>
          <Row noPadding marginBottom={spacing.small}>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              {/* TITLE */}
              <Col>
                <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                  <Col displayFlex>
                    <IconV2 name='channels' large />
                  </Col>
                  <Col>
                    <Text large bold tKey='RuleDetails' />
                  </Col>
                </Cols>
              </Col>
              <Col>
                <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                  {/* PREV/NEXT BUTTONS */}
                  <Col>
                    <Cols gap={spacing.extraSmall}>
                      <Col displayFlex>
                        <ButtonV2
                          small
                          style={{ paddingLeft: '12px' }}
                          clearBackground
                          disabled={!canNavigatePrev}
                          onClick={() => {
                            if (canNavigatePrev && currentIdx !== null) {
                              setCurrentIdx(currentIdx - 1);
                            }
                          }}
                        >
                          <IconV2
                            style={{
                              display: 'flex',
                              transform: 'rotate(90deg)',
                            }}
                            name='caret'
                          />
                        </ButtonV2>
                      </Col>
                      <Col displayFlex>
                        <ButtonV2
                          small
                          style={{ paddingRight: '12px' }}
                          clearBackground
                          disabled={!canNavigateNext}
                          onClick={() => {
                            if (canNavigateNext && currentIdx !== null) {
                              setCurrentIdx(currentIdx + 1);
                            }
                          }}
                        >
                          <IconV2
                            name='caret'
                            style={{ display: 'flex', transform: 'rotate(270deg)' }}
                          />
                        </ButtonV2>
                      </Col>
                    </Cols>
                  </Col>
                  {/* CLOSE BUTTON */}
                  <Col>
                    <CloseButton onClick={onClose} />
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Row>
          {/* SUMMARY */}
          {rule && (
            <Row noPadding>
              <Rows
                smallHorizontalPadding
                smallVerticalPadding
                style={highlightedDivStyle}
                gap={spacing.base}
              >
                <Row noPadding>
                  <Cols
                    gap={spacing.small}
                    style={{ alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => setIsSummaryOpen(!isSummaryOpen)}
                  >
                    <Col>
                      <Text small heavy disableSelect tKey='Summary' singleLineLabel />
                    </Col>
                    <Col displayFlex>
                      <IconV2
                        name='caret'
                        style={{ transform: isSummaryOpen ? 'rotate(180deg)' : 'none' }}
                      />
                    </Col>
                  </Cols>
                </Row>
                <Row noPadding style={{ display: isSummaryOpen ? 'block' : 'none' }}>
                  <Rows gap={spacing.small}>
                    {/* OVERVIEW */}
                    <Row noPadding>
                      <InventoryRuleOverview rule={rule} />
                    </Row>
                    {/* CONTENT */}
                    {rule.ContentData && (
                      <Row noPadding>
                        <ContentDataPreview data={rule} />
                      </Row>
                    )}
                    {/* CUSTOM */}
                    {rule.CustomsData && (
                      <Row noPadding>
                        <CustomDataPreview data={rule} />
                      </Row>
                    )}
                  </Rows>
                </Row>
              </Rows>
            </Row>
          )}
          {rule && rule.RuleType === InventoryRuleType.VMAP && !isEmpty(rule.AdBreaks) && (
            <Row noPadding>
              <Rows gap={spacing.small}>
                <Row noPadding>
                  <Text
                    small
                    style={{
                      padding: `${spacing.extraSmall} ${spacing.small}`,
                      backgroundColor: publicaColors.lighterBlue,
                      borderRadius: borderRadius.small,
                      width: 'fit-content',
                    }}
                    tKey='AdBreaks'
                  />
                </Row>
                <Row noPadding>
                  <ListViewTable
                    className='adbreaks-table'
                    loadState={loadState}
                    activeColumnKeys={adBreaksCfg.defaultState.activeColumnKeys}
                    columns={adBreaksCfg.columns}
                    order='asc'
                    orderBy=''
                    page={0}
                    rows={rows}
                    rowsPerPage={rows.length}
                    totalRowCount={rows.length}
                    handleOnClickColumnHeader={() => null}
                    handleChangePage={() => null}
                    handleChangeRowsPerPage={() => null}
                    isExpanded={false}
                    isReadAuthorized={true}
                    isWriteAuthorized={false}
                    key={`adbreaks-table-${rule.ID}`}
                    backgroundColor={publicaColors.veryLightGray}
                  />
                </Row>
              </Rows>
            </Row>
          )}
        </Rows>
      </Panel>
    </Drawer>
  );
};
